import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@client/lib/utils"

/**
 * Button — the single source of truth for interactive controls.
 *
 * Every app/portal/admin control should render through this component rather
 * than hand-rolling a `<button className="...">`. That guarantees three things
 * the audit found missing across ~150 raw buttons: a consistent height/shape,
 * a visible `focus-visible` ring, and a real disabled state.
 *
 * Warm Editorial uses Espresso-filled pills and warm card-surface outlines.
 * Icon controls stay square-rounded (12px); links have no radius. Add a variant
 * here when a new treatment is needed rather than hand-rolling a button.
 *
 * Pressed state: every filled or outlined variant darkens one ramp step and the
 * whole control settles 1px (`active:translate-y-px`); nothing scales. Under
 * `prefers-reduced-motion` the settle and the transition are dropped. Web only,
 * so there is no haptic — see docs/brand.md "Motion".
 */
const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-full text-sm font-medium whitespace-nowrap transition-[color,background-color,border-color,box-shadow,transform] duration-150 motion-reduce:transition-none active:translate-y-px motion-reduce:active:translate-y-0 outline-none focus-visible:ring-2 focus-visible:ring-espresso-600 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 aria-invalid:ring-2 aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "bg-espresso-600 text-white hover:bg-espresso-700 active:bg-espresso-800",
        /** `accent` is an alias of `default` (Espresso). */
        accent:
          "bg-espresso-600 text-white hover:bg-espresso-700 active:bg-espresso-800",
        destructive:
          "bg-red-600 text-white hover:bg-red-700 active:bg-red-800 focus-visible:ring-red-500",
        /** Needs-attention actions (past-due billing, blocked verification). */
        warning:
          "bg-amber-700 text-white hover:bg-amber-800 active:bg-amber-900 focus-visible:ring-amber-600",
        "destructive-outline":
          "border border-red-300 bg-transparent text-red-700 hover:bg-red-50 hover:border-red-400 active:bg-red-100 focus-visible:ring-red-500",
        outline:
          "border border-warm-200 bg-card text-warm-950 hover:border-warm-400 active:bg-warm-100 active:border-warm-400",
        "outline-neutral":
          "border border-warm-200 bg-card text-warm-950 hover:bg-warm-100 hover:border-warm-300 active:bg-warm-200",
        secondary: "bg-warm-100 text-warm-900 hover:bg-warm-200 active:bg-warm-300",
        neutral: "bg-warm-700 text-white hover:bg-warm-800 active:bg-warm-900",
        ghost: "text-warm-700 hover:bg-warm-100 hover:text-warm-950 active:bg-warm-200",
        link: "text-warm-950 border-b-[1.5px] border-transparent rounded-none hover:border-espresso-600 hover:text-espresso-700 active:translate-y-0",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        xs: "h-6 gap-1 px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 px-6 has-[>svg]:px-4",
        /** 52px — generous primary actions on editorial surfaces. */
        xl: "h-13 px-7 text-base",
        icon: "size-9 rounded-lg",
        "icon-xs": "size-6 rounded-lg [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8 rounded-lg",
        "icon-lg": "size-10 rounded-lg",
      },
    },
    compoundVariants: [
      { variant: "link", className: "rounded-none" },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
